import { useEffect, useState, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { isAdmin, isAuthenticated, getAccessToken, decodeJwtPayload } from '../../utils/api';
import { Spinner } from './shared';

const ADMIN_ENABLED = import.meta.env.VITE_ENABLE_ADMIN === 'true';
const MOBILE_BREAKPOINT = 768;

const RAG_SUB_ITEMS = [
    { path: '/admin/rag/overview', label: 'Overview' },
    { path: '/admin/rag/search', label: 'Search' },
    { path: '/admin/rag/manage', label: 'Collections' },
    { path: '/admin/rag/vector-stores', label: 'Vector Stores' },
];

const AGENT_SUB_ITEMS = [
    { path: '/admin/agents', label: 'Agents' },
    { path: '/admin/knowledge', label: 'Knowledge' },
];

const MONITOR_SUB_ITEMS = [
    { path: '/admin/monitor', label: 'Workers' },
    { path: '/admin/monitor/ws', label: 'WebSocket' },
    { path: '/admin/monitor/redis', label: 'Redis Cache' },
    { path: '/admin/monitor/local-cache', label: 'Local Cache' },
];

const NAV_SECTIONS = [
    {
        label: 'Overview',
        items: [
            { path: '/admin', label: 'Dashboard', icon: 'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z' },
        ],
    },
    {
        label: 'Configuration',
        items: [
            { path: '/admin/agents', label: 'Agents', icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z', subItems: AGENT_SUB_ITEMS },
            { path: '/admin/tools', label: 'Tools', icon: 'M11.42 15.17l-5.648-3.014a1.5 1.5 0 01-.404-2.366l6.95-6.95a1.5 1.5 0 012.366.404l3.014 5.648M11.42 15.17l5.648 3.014a1.5 1.5 0 00.404-2.366l-6.95-6.95a1.5 1.5 0 00-2.366.404L5.044 14.87M11.42 15.17L5.044 14.87' },
            { path: '/admin/skills', label: 'Skills', icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z' },
            { path: '/admin/subagents', label: 'Subagents', icon: 'M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z' },

            { path: '/admin/reminders', label: 'Reminders', icon: 'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0' },
            { path: '/admin/mcp', label: 'MCP Servers', icon: 'M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z' },
            { path: '/admin/rag', label: 'RAG System', icon: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25', subItems: RAG_SUB_ITEMS },
        ],
    },
    {
        label: 'Tasks',
        items: [
            { path: '/admin/scheduled-tasks', label: 'Tác vụ lập lịch', icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' },
            { path: '/admin/insight-categories', label: 'Loại báo cáo', icon: 'M3.75 6A2.25 2.25 0 016 3.75h12A2.25 2.25 0 0120.25 6v12A2.25 2.25 0 0118 20.25H6A2.25 2.25 0 013.75 18V6zM8.25 9h7.5m-7.5 3h7.5m-7.5 3h4.5' },
            { path: '/admin/feed-visibility', label: 'Quy tắc feed', icon: 'M12 3l9 4.5-9 4.5-9-4.5L12 3zm0 9l9 4.5-9 4.5-9-4.5L12 12z' },
            { path: '/admin/signal-outputs', label: 'Signal Outputs', icon: 'M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6' },
            { path: '/admin/instrument-intelligence', label: 'Instrument Insights', icon: 'M3 3v18h18M7 16l4-4 3 3 5-6' },
            { path: '/admin/instrument-profiles', label: 'Instrument Profiles', icon: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z' },
        ],
    },
    {
        label: 'Usage & Billing',
        items: [
            { path: '/admin/token-usage', label: 'Token Usage', icon: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z' },
            { path: '/admin/model-costs', label: 'Model Costs', icon: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z' },
            { path: '/admin/tier-window-limits', label: 'Window Limits', icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' },
            { path: '/admin/budget-adjustments', label: 'Budget Adj.', icon: 'M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
            { path: '/admin/budget-usage', label: 'Budget Usage', icon: 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z' },
            { path: '/admin/user-tiers', label: 'Users', icon: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z' },
        ],
    },
    {
        label: 'Monitoring',
        items: [
            { path: '/admin/sessions', label: 'Sessions', icon: 'M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155' },
            { path: '/admin/long-memory', label: 'Long Memory', icon: 'M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z' },
            { path: '/admin/content-urls', label: 'Content URLs', icon: 'M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13' },
            { path: '/admin/artifacts', label: 'Artifacts', icon: 'M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6' },
            { path: '/admin/shares', label: 'Shares', icon: 'M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z' },
            { path: '/admin/checkpoints', label: 'Checkpoints', icon: 'M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z' },
            { path: '/admin/hub', label: 'Hub', icon: 'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0' },
            { path: '/admin/sandbox', label: 'Sandbox', icon: 'M6.75 7.5l3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0021 18V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v12a2.25 2.25 0 002.25 2.25z' },
            { path: '/admin/crawler', label: 'Crawler Test', icon: 'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418' },
            { path: '/admin/ocr', label: 'OCR Parser', icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z' },
            { path: '/admin/monitor', label: 'Server Monitor', icon: 'M21.75 17.25v-.228a4.5 4.5 0 00-.12-1.03l-2.268-9.64a3.375 3.375 0 00-3.285-2.602H7.923a3.375 3.375 0 00-3.285 2.602l-2.268 9.64a4.5 4.5 0 00-.12 1.03v.228m19.5 0a3 3 0 01-3 3H5.25a3 3 0 01-3-3m19.5 0a3 3 0 00-3-3H5.25a3 3 0 00-3 3m16.5 0h.008v.008h-.008v-.008zm-3 0h.008v.008h-.008v-.008z', subItems: MONITOR_SUB_ITEMS },
        ],
    },
    {
        label: 'Operations',
        items: [
            { path: '/admin/system-configs', label: 'System Configs', icon: 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z' },
            { path: '/admin/sync', label: 'Sync & Deploy', icon: 'M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z' },
            { path: '/admin/sync-configs', label: 'Sync Configs', icon: 'M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12' },
            { path: '/admin/credentials', label: 'Credentials', icon: 'M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z' },
            { path: '/admin/environments', label: 'Environments', icon: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z' },
            { path: '/admin/api-keys', label: 'API Keys', icon: 'M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z' },
            { path: '/admin/gemini-caches', label: 'Gemini Cache', icon: 'M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125' },
        ],
    },
];

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);

    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
        const handler = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    return isMobile;
}

function MenuIcon() {
    return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
    );
}

function CollapseIcon({ collapsed }) {
    return (
        <svg className={`w-4 h-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.75 19.5l-7.5-7.5 7.5-7.5m-6 15L5.25 12l7.5-7.5" />
        </svg>
    );
}

function SidebarContent({ collapsed, user, isActive, isSubActive, onNavClick, onToggle }) {
    return (
        <>
            <div className={`border-b border-gray-100 flex items-center ${collapsed ? 'px-2 py-4 justify-center' : 'px-4 py-5 justify-between'}`}>
                <Link to="/admin" className="flex items-center gap-2" onClick={onNavClick}>
                    <div className="h-7 w-7 rounded-lg bg-gray-900 flex items-center justify-center shrink-0">
                        <span className="text-white text-xs font-bold">S</span>
                    </div>
                    {!collapsed && <span className="text-sm font-semibold text-gray-900">Admin Console</span>}
                </Link>
                {onToggle && !collapsed && (
                    <button
                        type="button"
                        onClick={onToggle}
                        className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        title="Collapse sidebar"
                    >
                        <CollapseIcon collapsed={false} />
                    </button>
                )}
            </div>

            <nav className={`flex-1 py-4 space-y-6 overflow-y-auto ${collapsed ? 'px-1.5' : 'px-3'}`}>
                {collapsed && onToggle && (
                    <div className="flex justify-center mb-2">
                        <button
                            type="button"
                            onClick={onToggle}
                            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            title="Expand sidebar"
                        >
                            <CollapseIcon collapsed={true} />
                        </button>
                    </div>
                )}
                {NAV_SECTIONS.map((section) => (
                    <div key={section.label}>
                        {!collapsed && (
                            <p className="px-3 mb-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                                {section.label}
                            </p>
                        )}
                        <ul className="space-y-0.5">
                            {section.items.map((item) => (
                                <li key={item.path}>
                                    <Link
                                        to={item.subItems ? item.subItems[0].path : item.path}
                                        title={collapsed ? item.label : undefined}
                                        className={`flex items-center rounded-md text-sm transition-colors ${
                                            collapsed
                                                ? 'justify-center p-2'
                                                : 'gap-2.5 px-3 py-2'
                                        } ${
                                            isActive(item)
                                                ? 'bg-gray-100 text-gray-900 font-medium'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                        onClick={onNavClick}
                                    >
                                        <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                                        </svg>
                                        {!collapsed && <span>{item.label}</span>}
                                    </Link>
                                    {!collapsed && item.subItems && isActive(item) && (
                                        <ul className="ml-6 mt-0.5 space-y-0.5 border-l border-gray-200 pl-2">
                                            {item.subItems.map((sub) => (
                                                <li key={sub.path}>
                                                    <Link
                                                        to={sub.path}
                                                        className={`block px-2.5 py-1.5 rounded text-xs transition-colors ${
                                                            isSubActive(sub.path)
                                                                ? 'text-gray-900 font-medium bg-gray-50'
                                                                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                                                        }`}
                                                        onClick={onNavClick}
                                                    >
                                                        {sub.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </nav>

            <div className={`border-t border-gray-100 ${collapsed ? 'px-1.5 py-3' : 'px-3 py-4'}`}>
                {!collapsed && (
                    <div className="px-3 mb-3">
                        <p className="text-sm text-gray-900 font-medium truncate">{user?.email}</p>
                        <p className="text-xs text-gray-400 truncate">UID: {user?.uid}</p>
                    </div>
                )}
                <Link
                    to="/"
                    title={collapsed ? 'Back to Chat' : undefined}
                    className={`flex items-center rounded-md text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors ${
                        collapsed ? 'justify-center p-2' : 'gap-2 px-3 py-2'
                    }`}
                >
                    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                    </svg>
                    {!collapsed && 'Back to Chat'}
                </Link>
            </div>
        </>
    );
}

export default function AdminLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        if (!ADMIN_ENABLED) {
            navigate('/');
            return;
        }

        if (!isAuthenticated()) {
            navigate('/?login=required');
            return;
        }

        if (!isAdmin()) {
            navigate('/?error=admin_required');
            return;
        }

        const token = getAccessToken();
        if (token) {
            const payload = decodeJwtPayload(token);
            setUser({
                email: payload.sub,
                uid: payload.uid,
                roles: payload.auth,
            });
        }

        setLoading(false);
    }, [navigate]);

    useEffect(() => {
        if (mobileOpen) setMobileOpen(false);
    }, [location.pathname]);

    const closeMobile = useCallback(() => setMobileOpen(false), []);

    if (!ADMIN_ENABLED) return null;

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Spinner size="lg" text="Loading admin..." />
            </div>
        );
    }

    const isSubActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    const isActive = (item) => {
        if (item.path === '/admin') return location.pathname === '/admin';
        if (item.subItems) return item.subItems.some((sub) => isSubActive(sub.path));
        return location.pathname.startsWith(item.path);
    };

    const sidebarProps = { user, isActive, isSubActive };

    return (
        <div className="min-h-screen bg-gray-50">
            {isMobile && (
                <header className="sticky top-0 z-30 flex items-center gap-3 bg-white border-b border-gray-200 px-4 py-3">
                    <button
                        type="button"
                        onClick={() => setMobileOpen(true)}
                        className="p-1 -ml-1 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                        <MenuIcon />
                    </button>
                    <Link to="/admin" className="flex items-center gap-2">
                        <div className="h-6 w-6 rounded-md bg-gray-900 flex items-center justify-center">
                            <span className="text-white text-[10px] font-bold">S</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-900">Admin Console</span>
                    </Link>
                </header>
            )}

            {isMobile && mobileOpen && (
                <div className="fixed inset-0 z-40 flex">
                    <div className="fixed inset-0 bg-black/40" onClick={closeMobile} />
                    <aside className="relative z-50 w-72 max-w-[85vw] h-full bg-white flex flex-col shadow-xl animate-slide-in-left">
                        <div className="absolute top-3 right-3">
                            <button
                                type="button"
                                onClick={closeMobile}
                                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                <CloseIcon />
                            </button>
                        </div>
                        <SidebarContent {...sidebarProps} collapsed={false} onNavClick={closeMobile} />
                    </aside>
                </div>
            )}

            <div className="flex">
                {!isMobile && (
                    <aside className={`h-screen bg-white border-r border-gray-200 flex flex-col shrink-0 sticky top-0 transition-all duration-200 ${collapsed ? 'w-14' : 'w-56'}`}>
                        <SidebarContent {...sidebarProps} collapsed={collapsed} onNavClick={undefined} onToggle={() => setCollapsed((v) => !v)} />
                    </aside>
                )}

                <main className="flex-1 min-h-screen min-w-0">
                    <div className="w-full px-3 py-4 md:px-6 md:py-8">
                        <Outlet />
                    </div>
                </main>
            </div>
            <Toaster position="top-right" />
        </div>
    );
}
